<template>
  <div class="search-panel-component">
    <!-- Search Panel -->
    <div class="search-panel-mask" :class="{ show: isOpen }" @click="closePanel"></div>
    <div class="search-panel" role="search" :class="{ show: isOpen }" :aria-hidden="!isOpen">
      <button class="search-back" aria-label="返回网站" @click="closePanel">
        <svg viewBox="0 0 24 24" focusable="false">
          <path d="M0 0h24v24H0z" fill="none"></path>
          <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z"></path>
        </svg>
      </button>

      <form class="search-form" @submit.prevent="handleSearch">
        <div class="form-wrap">
          <button type="submit" class="search-submit" aria-label="Submit search" v-show="!isLoading">
            <svg class="vu8Pwe" viewBox="0 0 24 24" focusable="false">
              <path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"></path>
              <path d="M0 0h24v24H0z" fill="none"></path>
            </svg>
          </button>

          <button
            type="button"
            role="button"
            class="mobile-search-back"
            aria-label="返回网站"
            @click="closePanel">
            <svg viewBox="0 0 24 24" focusable="false">
              <path d="M0 0h24v24H0z" fill="none"></path>
              <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z"></path>
            </svg>
          </button>

          <div class="search-loading" v-show="isLoading">
            <div class="spinner"></div>
          </div>

          <div class="search-input-box">
            <div class="input-wrap">
              <input
                type="search"
                class="search-input"
                v-model="searchQuery"
                aria-label="搜索此网站"
                autocomplete="off"
                :placeholder="placeholder"
                @keydown="handleKeydown" />
            </div>
          </div>

          <div class="search-close-wrap">
            <div
              role="button"
              class="search-close"
              aria-label="清除搜索"
              :aria-disabled="!searchQuery"
              @click="clearSearch">
              <svg class="fAUEUd" viewBox="0 0 24 24" focusable="false">
                <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"></path>
                <path d="M0 0h24v24H0z" fill="none"></path>
              </svg>
            </div>
          </div>
        </div>
      </form>
    </div>

    <!-- Search Results -->
    <div class="search-results" :class="{ show: showResults }">
      <div class="search-results-wrap" v-show="!isLoading">
        <div class="search-results-title">来自此网站的结果</div>

        <!-- Loading 状态 -->
        <div class="search-loading" v-show="isLoading">
          <div class="spinner"></div>
          <span>搜索中...</span>
        </div>

        <!-- 有结果时显示 -->
        <div class="search-results-item-wrap" v-if="results.length > 0">
          <div
            class="search-results-item"
            v-for="result in results"
            :key="result.url">
            <a :href="'/' + result.url" class="search-results-item-link" v-html="highlightMatch(result.displayTitle || result.title || '', searchQuery)"></a>
            <div class="search-results-item-description" v-html="highlightMatch(truncate(result.displayContent || result.content || '', 100), searchQuery)"></div>
          </div>
        </div>

        <!-- 无结果时显示 -->
        <div class="search-no-results" v-show="showNoResults">
          <div class="no-result-title">没有与您搜索条件相符的结果</div>
          <img class="img-wrap" :src="noResultsImage" />
          <div class="no-result-tip">请检查拼写或尝试其他搜索字词</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'SearchPanel',

  props: {
    apiEndpoint: {
      type: String,
      default: '/api/v1/search'
    },
    placeholder: {
      type: String,
      default: '搜索此网站'
    },
    noResultsImage: {
      type: String,
      default: ''
    },
    initialQuery: {
      type: String,
      default: ''
    }
  },

  data() {
    return {
      isOpen: false,
      searchQuery: this.initialQuery,
      isLoading: false,
      results: [],
      showResults: false,
      showNoResults: false
    };
  },

  mounted() {
    if (this.initialQuery) {
      this.handleSearch();
    }
  },

  methods: {
    handleSearch() {
      const query = this.searchQuery.trim();
      if (!query || this.isLoading) return;

      this.search(query);
    },

    async search(query) {
      try {
        this.isLoading = true;
        this.showResults = true;
        this.showNoResults = false;
        this.results = [];

        const params = new URLSearchParams({
          q: query,
          limit: 10
        });

        const response = await fetch(`${this.apiEndpoint}?${params}`);

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();

        if (!data.success) {
          console.error('[SearchPanel] Search failed:', data.error);
          this.showNoResults = true;
          return;
        }

        this.results = data.results || [];

        if (this.results.length === 0) {
          this.showNoResults = true;
        }
      } catch (error) {
        console.error('[SearchPanel] Search error:', error);
        this.showNoResults = true;
      } finally {
        this.isLoading = false;
      }
    },

    clearSearch() {
      this.searchQuery = '';
      this.results = [];
      this.showResults = false;
      this.showNoResults = false;
    },

    closePanel() {
      this.isOpen = false;
      this.clearSearch();
      if (window.navigationMenu && window.navigationMenu.isSearchOpen) {
        window.navigationMenu.isSearchOpen = false;
        window.navigationMenu.searchToggle?.setAttribute('aria-expanded', 'false');
      }
    },

    openPanel() {
      this.isOpen = true;
    },

    handleKeydown(e) {
      if (e.key === 'Escape') {
        this.clearSearch();
        this.closePanel();
      }
    },

    highlightMatch(text, query) {
      if (!text) return '';

      const escapedQuery = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const regex = new RegExp(`(${escapedQuery})`, 'gi');

      return text.replace(regex, '<b>$1</b>');
    },

    truncate(text, length) {
      if (!text) return '';
      if (text.length <= length) return text;
      return text.substring(0, length) + '...';
    }
  }
};
</script>
